import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, merge, of, switchMap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { SupplierService } from '@ps/survey-data-access';

import {
  ManageSurveyActions,
  ManageSurveyApiActions,
  ManageSurveySiblingApiActions,
} from '../manage-survey/manage-survey.actions';
import {
  ManageSurveyReferenceActions,
  ManageSurveyReferenceApiActions,
} from './manage-survey-reference.actions';

@Injectable()
export class ManageSurveyReferenceEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _supplierService = inject(SupplierService);

  // A CPI edit or a child-survey switch changes what the allocations look
  // like, so both refetch alongside the explicit load
  public loadSupplierAllocations$ = createEffect(() => {
    const bySurveyId$ = this._actions$.pipe(
      ofType(
        ManageSurveyReferenceActions.loadSupplierAllocations,
        ManageSurveyActions.loadChildSurveyDetails,
      ),
      map(({ surveyId }) => surveyId),
    );

    const bySurvey$ = this._actions$.pipe(
      ofType(
        ManageSurveySiblingApiActions.updateCurrentCpiSuccess,
        ManageSurveySiblingApiActions.updateChildCurrentCpiSuccess,
        ManageSurveyApiActions.loadChildSurveySuccess,
      ),
      map(({ survey }) => survey.id),
    );

    return merge(bySurveyId$, bySurvey$).pipe(
      switchMap((surveyId) =>
        this._supplierService.allocations(surveyId, { scope: 'view' }).pipe(
          map((supplierAllocations) =>
            ManageSurveyReferenceApiActions.loadSupplierAllocationsSuccess({
              supplierAllocations,
            }),
          ),
          this.psCatchError(
            (err) =>
              of(
                ManageSurveyReferenceApiActions.loadSupplierAllocationsFailure({
                  error: err.message ?? 'Failed to load supplier allocations',
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    );
  });
}
